import L from 'leaflet';
import {
    splitPhones, normalizeDigits, isItalianMobileDigits,
    buildWhatsappUrl, trackPoiContact, API_NODE_BASE
} from '../utils/contactUtils.js';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import { point } from '@turf/helpers';


// Funzione di traduzione globale per tutto il file (deve essere in cima)
const getLang = () => (window.i18next && window.i18next.language) || localStorage.getItem('lng') || 'it';
const t = (key) => {
    const lang = getLang();
    const dict = {
        'info_mappa': { it: '📋 Info mappa', en: '📋 Map info' },
        'info_aggiuntive': { it: 'ℹ️ Info aggiuntive', en: 'ℹ️ Additional info' },
        'telefono': { it: '📞 Telefono', en: '📞 Phone' },
        'mobile': { it: '📱 Mobile', en: '📱 Mobile' },
        'fisso': { it: '📞 Fisso', en: '📞 Landline' },
        'email': { it: '📧 Email', en: '📧 Email' },
        'sito': { it: '🌐 Sito', en: '🌐 Website' },
        'visita_sito': { it: 'Visita sito', en: 'Visit site' },
        'altitudine': { it: '🗻 Altitudine', en: '🗻 Altitude' },
        'tipo': { it: '🏷️ Tipo', en: '🏷️ Type' },
        'categoria': { it: '🏷️ Categoria', en: '🏷️ Category' },
        'orari': { it: '🕐 Orari', en: '🕐 Opening hours' },
        'fonte_osm': { it: '📊 Fonte: OpenStreetMap', en: '📊 Source: OpenStreetMap' },
        'maps': { it: '🗺️ Maps', en: '🗺️ Maps' },
        'copia_link': { it: '🔗 Copia Link', en: '🔗 Copy Link' },
        'copiato': { it: '✅ Copiato!', en: '✅ Copied!' },
        'aggiungi': { it: '✏️ Aggiungi', en: '✏️ Add' },
        'nei_dintorni': { it: '🌄 Nei dintorni', en: '🌄 Nearby' },
        'percorsi': { it: '🥾 Percorsi', en: '🥾 Trails' },
        'caricamento_info': { it: 'Caricamento info aggiuntive...', en: 'Loading additional info...' },
        'caricamento': { it: 'Caricamento...', en: 'Loading...' },
        'nessun_risultato': { it: 'Nessun risultato trovato.', en: 'No results found.' },
        'luoghi_outdoor': { it: 'Luoghi outdoor nei dintorni', en: 'Nearby outdoor places' },
        'risultati_entro': { it: 'Risultati entro', en: 'Results within' },
        'da_questo_punto': { it: 'km da questo punto.', en: 'km from this point.' },
        // Tipi attività
        'alloggio': { it: 'Alloggio', en: 'Accommodation' },
        'altro': { it: 'Altro', en: 'Other' },
        'rifugio': { it: 'Rifugio', en: 'Mountain hut' },
        'ristorazione': { it: 'Ristorazione', en: 'Food & Drink' },
    };
    return (dict[key] && dict[key][lang]) || dict[key]?.it || key;
};

const API_BASE_URL = process.env.REACT_APP_API_URL_NODE;
const API_PYTHON_URL = process.env.REACT_APP_API_URL_PYTHON || 'http://localhost:5001';

// Colori per categorie POI
const getCategoryColor = (category) => {
    const colorMap = {
        sport_archery: '#8D6E63', sport_horse: '#6D4C41', sport_ski: '#90CAF9',
        sport_paragliding: '#CE93D8', sport_climbing: '#FF7043', adventure_park: '#FFB300',
        suspension_bridge: '#4ECDC4', alpine_hut: '#FF7043', bivouac: '#A1887F',
        shelter: '#8D6E63', peak: '#81C784', lake: '#64B5F6', waterfall: '#2196F3',
        spring: '#4FC3F7', cave: '#795548', parking: '#9B59B6', viewpoint: '#F39C12',
        church: '#95E1D3', ruins: '#9E9E9E', generic: '#4CAF50'
    };
    return colorMap[category] || colorMap.generic;
};

if (typeof document !== 'undefined' && !document.getElementById('poi-marker-styles')) {
    const styleSheet = document.createElement('style');
    styleSheet.id = 'poi-marker-styles';
    styleSheet.textContent = `
        .leaflet-div-icon { display: block !important; background: transparent !important; border: none !important; }
        .poi-marker-simple {
            background: white; border: 2px solid #4CAF50; border-radius: 50%;
            width: 32px; height: 32px; display: flex; align-items: center;
            justify-content: center; box-shadow: 0 2px 6px rgba(0,0,0,0.3);
            cursor: pointer; transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .poi-marker-simple:hover { transform: scale(1.15); box-shadow: 0 3px 10px rgba(0,0,0,0.4); }
        .poi-marker-simple.poi-alert { border-color: #C62828; }
        .poi-marker-simple.poi-commercial { border-color: #FFC107; }
        @keyframes poi-pulse {
            0%, 100% { transform: scale(1); box-shadow: 0 2px 6px rgba(198,40,40,0.3); }
            50% { transform: scale(1.1); box-shadow: 0 3px 10px rgba(198,40,40,0.6); }
        }
    `;
    document.head.appendChild(styleSheet);
}

export const createCustomMarker = (props, categoryIcon) => {
    const emoji = categoryIcon[props.poiCategory] || '📍';
    const color = getCategoryColor(props.poiCategory);
    let visualClass = '';
    switch (props.poiVisualType) {
        case 'alert': visualClass = 'poi-alert'; break;
        case 'commercial': visualClass = 'poi-commercial'; break;
        default: visualClass = '';
    }
    const html = `
        <div class="poi-marker-simple ${visualClass}" style="
            background: white; border: 2px solid ${color}; border-radius: 50%;
            width: 32px; height: 32px; display: flex; align-items: center;
            justify-content: center; box-shadow: 0 2px 6px rgba(0,0,0,0.3); cursor: pointer;">
            <span style="font-size: 20px; line-height: 1;">${emoji}</span>
        </div>`;
    return L.divIcon({
        className: 'leaflet-div-icon poi-marker-simple',
        html, iconSize: [32, 32], iconAnchor: [16, 32], popupAnchor: [0, -32]
    });
};

// ─── Phone helpers ────────────────────────────────────────────────────────────



// ─── Tracking helpers ─────────────────────────────────────────────────────────

function trackContact(type, osmid, lat, lng) {
    const url = `${API_NODE_BASE}/go/${type}?osmid=${encodeURIComponent(osmid)}&lat=${encodeURIComponent(lat)}&lng=${encodeURIComponent(lng)}`;
    fetch(url, { method: 'GET', keepalive: true })
        .catch(err => console.warn(`[${type}] Tracking error:`, err.message));
}

// ─── createDynamicPopup ───────────────────────────────────────────────────────

export const createDynamicPopup = (props, lat, lng, featureId, poi, callbacks = {}) => {
    const popupContainer = document.createElement('div');
    popupContainer.className = 'poi-popup-dynamic';
    popupContainer.style.cssText = 'min-width: 250px; max-width: 320px; max-height: 400px; overflow-y: auto;';

    const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
    const showFullInfoBtn = typeof callbacks.onShowFullInfo === 'function';
    const showAddUpdateBtn = typeof callbacks.onAddUpdate === 'function';

    // Traduzioni base IT/EN per popup (puoi estendere se servono altre chiavi)
    // Funzione di traduzione globale per tutto il file

    const cleanOsmid = (featureId || '').replace(/^(node|way|relation)\//, '');

    // ── Genera bottoni contatto per il tab OSM ────────────────────────────────
    let osmContactsHTML = '';

    // Telefoni
    const phoneFields = [
        { label: t('telefono'), value: props.phone },
        { label: t('mobile'), value: props['contact:mobile'] },
        { label: t('fisso'), value: props['contact:phone'] },
    ];
    for (const { label, value } of phoneFields) {
        if (!value) continue;
        for (const part of splitPhones(value)) {
            osmContactsHTML += `
                <p style="margin: 4px 0; font-size: 12px;">
                    <strong>${label}:</strong>
                    <button class="osm-phone-btn" data-phone="${part}"
                        data-osmid="${cleanOsmid}" data-lat="${lat}" data-lng="${lng}"
                        style="background:none;border:none;padding:0;color:#2196F3;font-size:12px;cursor:pointer;text-decoration:underline;">
                        ${part}
                    </button>
                </p>`;
        }
    }

    // Email
    const emailVal = props.email || props['contact:email'];
    if (emailVal) {
        osmContactsHTML += `
            <p style="margin: 4px 0; font-size: 12px;">
                <strong>${t('email')}:</strong>
                <button class="osm-email-btn" data-email="${emailVal}"
                    data-osmid="${cleanOsmid}" data-lat="${lat}" data-lng="${lng}"
                    style="background:none;border:none;padding:0;color:#2196F3;font-size:12px;cursor:pointer;text-decoration:underline;">
                    ${emailVal}
                </button>
            </p>`;
    }

    // Website — tracciato via /go/website
    const websiteVal = props.website || props['contact:website'];
    if (websiteVal) {
        osmContactsHTML += `
            <p style="margin: 4px 0; font-size: 12px;">
                <strong>${t('sito')}:</strong>
                <button class="osm-website-btn" data-website="${websiteVal}"
                    data-osmid="${cleanOsmid}" data-lat="${lat}" data-lng="${lng}"
                    style="background:none;border:none;padding:0;color:#2196F3;font-size:12px;cursor:pointer;text-decoration:underline;">
                    ${t('visita_sito')}
                </button>
            </p>`;
    }

    const linkToCopy = `${window.location.origin}${window.location.pathname}#poi-${featureId}`;
    popupContainer.innerHTML = `
        <div class="popup-header" style="margin-bottom: 10px;">
            <h3 style="margin: 0; font-size: 16px; color: #333;">${props.name || 'Punto di interesse'}</h3>
        </div>

        <div class="popup-tabs" style="display: flex; gap: 4px; margin-bottom: 10px; border-bottom: 1px solid #ddd;">
            <button class="tab-btn active" data-tab="osm"
                style="flex: 1; padding: 6px 10px; background: none; border: none; border-bottom: 2px solid #2196F3; cursor: pointer; font-size: 12px; font-weight: 600; color: #2196F3;">
                ${t('info_mappa')}
            </button>
            <button class="tab-btn" data-tab="additional" id="tab-additional-btn"
                style="flex: 1; padding: 6px 10px; background: none; border: none; border-bottom: 2px solid transparent; cursor: pointer; font-size: 12px; color: #666;">
                ${t('info_aggiuntive')}
            </button>
        </div>

        <div class="tab-content active" data-tab-content="osm" style="min-height: 60px;">
            ${props.description ? `
                <div style="margin: 6px 0; padding: 8px; background: #f9f9f9; border-radius: 4px; border-left: 2px solid #2196F3; font-size: 12px; line-height: 1.5; color: #333;">
                    ${props.description}
                </div>` : ''}
            ${props.ele ? `<p style="margin: 4px 0; font-size: 12px;"><strong>${t('altitudine')}:</strong> ${props.ele}m</p>` : ''}
            ${props.type ? `<p style="margin: 4px 0; font-size: 12px;"><strong>${t('tipo')}:</strong> ${props.type}</p>` : ''}
            ${props.amenity ? `<p style="margin: 4px 0; font-size: 12px;"><strong>${t('categoria')}:</strong> ${props.amenity}</p>` : ''}
            ${props.opening_hours ? `<p style="margin: 4px 0; font-size: 12px;"><strong>${t('orari')}:</strong> ${props.opening_hours}</p>` : ''}
            ${osmContactsHTML}
            <p style="font-size: 10px; color: #999; margin-top: 10px; padding-top: 8px; border-top: 1px solid #eee;">${t('fonte_osm')}</p>
            <div style="margin-top: 10px; display: flex; gap: 6px; flex-wrap: wrap;">
                <a href="${googleMapsUrl}" target="_blank" rel="noopener noreferrer"
                    style="display: inline-block; padding: 6px 12px; background: #4285F4; color: white; text-decoration: none; border-radius: 4px; font-size: 12px; font-weight: 500;">
                    ${t('maps')}
                </a>
                <button class="btn-copy-link" title="Copia link diretto" style="padding: 6px 12px; background: #607D8B; color: white; border: none; border-radius: 4px; font-size: 12px; font-weight: 500; cursor: pointer;">
                    ${t('copia_link')}
                </button>
                ${showFullInfoBtn ? `
                    <button class="btn-show-full-info"
                        style="padding: 6px 12px; background: #FF9800; color: white; border: none; border-radius: 4px; font-size: 12px; font-weight: 500; cursor: pointer;">
                        ${t('info_mappa')}
                    </button>` : ''}
                ${showAddUpdateBtn ? `
                    <button class="btn-add-update-info"
                        style="padding: 6px 12px; background: #4CAF50; color: white; border: none; border-radius: 4px; font-size: 12px; font-weight: 500; cursor: pointer;">
                        ${t('aggiungi')}
                    </button>` : ''}
                <button class="btn-nearby-places"
                    style="padding: 6px 12px; background: #1976d2; color: white; border: none; border-radius: 4px; font-size: 12px; font-weight: 500; cursor: pointer;">
                    ${t('nei_dintorni')}
                </button>
                <button class="btn-show-trails"
                    style="padding: 6px 12px; background: #388E3C; color: white; border: none;
                           border-radius: 4px; font-size: 12px; font-weight: 500; cursor: pointer;">
                    ${t('percorsi')}
                </button>
            </div>
        </div>

        <div class="tab-content" data-tab-content="additional" style="display: none; min-height: 60px;">
            <div style="text-align: center; padding: 15px; color: #666;">
                <div style="display: inline-block; width: 20px; height: 20px; border: 2px solid #f3f3f3; border-top: 2px solid #4CAF50; border-radius: 50%; animation: spin 1s linear infinite;"></div>
                <style>@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }</style>
                <p style="margin-top: 10px; font-size: 12px;">${t('caricamento_info')}</p>
            </div>
        </div>
    `;
    // Handler per il pulsante "Copia Link"
    const btnCopyLink = popupContainer.querySelector('.btn-copy-link');
    if (btnCopyLink) {
        btnCopyLink.addEventListener('click', async () => {
            try {
                await navigator.clipboard.writeText(linkToCopy);
                btnCopyLink.textContent = t('copiato');
                setTimeout(() => { btnCopyLink.textContent = t('copia_link'); }, 1200);
            } catch (err) {
                btnCopyLink.textContent = 'Errore';
                setTimeout(() => { btnCopyLink.textContent = t('copia_link'); }, 1200);
            }
        });
    }
    // Handler per il pulsante "Nei dintorni"
    const btnNearby = popupContainer.querySelector('.btn-nearby-places');
    if (btnNearby) {
        btnNearby.addEventListener('click', async () => {
            const radius_km = 10;
            btnNearby.disabled = true;
            btnNearby.textContent = t('caricamento');
            try {
                const resp = await fetch(`${API_PYTHON_URL}/api/outdoor-places`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ lat, lon: lng, radius_km, lang: getLang() })
                });
                if (!resp.ok) throw new Error('Errore server: ' + resp.status);
                const data = await resp.json();
                showNearbyResultsModal(data.places || [], lat, lng, radius_km);
            } catch (err) {
                alert('Errore durante la ricerca dei luoghi nei dintorni: ' + (err.message || err));
            } finally {
                btnNearby.disabled = false;
                btnNearby.textContent = '🌄 Nei dintorni';
            }
        });
    }

    // Funzione per mostrare i risultati in una nuova finestra/modal
    function showNearbyResultsModal(places, lat, lng, radius_km) {
        const modal = document.createElement('div');
        modal.style.cssText = 'position:fixed;z-index:9999;top:0;left:0;width:100vw;height:100vh;background:rgba(0,0,0,0.45);display:flex;align-items:center;justify-content:center;';
        const content = document.createElement('div');
        content.style.cssText = 'background:#fff;max-width:600px;width:95vw;max-height:90vh;overflow-y:auto;border-radius:8px;box-shadow:0 4px 24px rgba(0,0,0,0.25);padding:24px 18px 18px 18px;position:relative;';
        content.innerHTML = `
            <button style="position:absolute;top:10px;right:10px;font-size:18px;background:none;border:none;cursor:pointer;color:#888;" title="Chiudi">✖️</button>
            <h2 style="margin-top:0;font-size:20px;color:#1976d2;">${t('luoghi_outdoor')}</h2>
            <p style="font-size:13px;color:#555;margin-bottom:16px;">${t('risultati_entro')} ${radius_km} ${t('da_questo_punto')}</p>
            <div style="max-height:60vh;overflow-y:auto;">
                ${places.length === 0 ? `<p style="color:#888;">${t('nessun_risultato')}</p>` :
                places.map(place => `
                        <div style="margin-bottom:18px;padding-bottom:12px;border-bottom:1px solid #eee;">
                            <a href="${place.url}" target="_blank" rel="noopener noreferrer" style="font-size:15px;font-weight:600;color:#1976d2;text-decoration:none;">${place.title}</a>
                            <p style="font-size:12px;color:#333;margin:6px 0 0 0;">${place.snippet || ''}</p>
                            <a href="${place.url}" target="_blank" rel="noopener noreferrer" style="font-size:11px;color:#888;word-break:break-all;">${place.url}</a>
                        </div>
                    `).join('')
            }
            </div>
        `;
        // Chiudi modal
        content.querySelector('button').onclick = () => document.body.removeChild(modal);
        modal.appendChild(content);
        modal.addEventListener('click', e => { if (e.target === modal) document.body.removeChild(modal); });
        document.body.appendChild(modal);
    }

    // ── Tab switching ─────────────────────────────────────────────────────────
    const tabButtons = popupContainer.querySelectorAll('.tab-btn');
    const tabContents = popupContainer.querySelectorAll('.tab-content');
    tabButtons.forEach(btn => {
        btn.addEventListener('click', () => {
            const tabName = btn.getAttribute('data-tab');
            tabButtons.forEach(b => {
                b.classList.remove('active');
                b.style.borderBottomColor = 'transparent';
                b.style.color = '#666';
                b.style.fontWeight = '400';
            });
            tabContents.forEach(c => { c.classList.remove('active'); c.style.display = 'none'; });
            btn.classList.add('active');
            btn.style.borderBottomColor = '#2196F3';
            btn.style.color = '#2196F3';
            btn.style.fontWeight = '600';
            const target = popupContainer.querySelector(`[data-tab-content="${tabName}"]`);
            if (target) { target.classList.add('active'); target.style.display = 'block'; }
        });
    });


    if (showFullInfoBtn) {
        popupContainer.querySelector('.btn-show-full-info')
            .addEventListener('click', () => callbacks.onShowFullInfo(featureId, poi));
    }
    if (showAddUpdateBtn) {
        popupContainer.querySelector('.btn-add-update-info')
            .addEventListener('click', () => callbacks.onAddUpdate(featureId, poi));
    }

    // ── Percorsi vicini ───────────────────────────────────────────────────────
    const btnShowTrails = popupContainer.querySelector('.btn-show-trails');
    if (btnShowTrails) {
        btnShowTrails.addEventListener('click', () => {
            const pathParts = window.location.pathname.split('/');
            // URL atteso: /region/{name}/... → pathParts[2] = region name
            const region = pathParts[1] === 'region' ? pathParts[2] : null;
            if (!region) return;
            const poiName = props.name ? `&name=${encodeURIComponent(props.name)}` : '';
            window.location.href = `/region/${region}/trekkings?poi=${lat},${lng}&zoom=15${poiName}`;
        });
    }

    // ── Contatti OSM tab: telefono ────────────────────────────────────────────
    popupContainer.querySelectorAll('.osm-phone-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.preventDefault(); e.stopPropagation();
            const phone = btn.getAttribute('data-phone');
            window.location.href = `tel:${phone}`;
            trackContact('phone', btn.getAttribute('data-osmid'), btn.getAttribute('data-lat'), btn.getAttribute('data-lng'));
        });
    });

    // ── Contatti OSM tab: email ───────────────────────────────────────────────
    popupContainer.querySelectorAll('.osm-email-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.preventDefault(); e.stopPropagation();
            window.location.href = `mailto:${btn.getAttribute('data-email')}`;
            trackContact('email', btn.getAttribute('data-osmid'), btn.getAttribute('data-lat'), btn.getAttribute('data-lng'));
        });
    });

    // ── Contatti OSM tab: website ─────────────────────────────────────────────
    popupContainer.querySelectorAll('.osm-website-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.preventDefault(); e.stopPropagation();
            window.open(btn.getAttribute('data-website'), '_blank', 'noopener,noreferrer');
            trackPoiContact('website', btn.getAttribute('data-osmid'), btn.getAttribute('data-lat'), btn.getAttribute('data-lng'));
        });
    });

    popupContainer.dataset.lat = lat;
    popupContainer.dataset.lng = lng;
    popupContainer.dataset.osmid = cleanOsmid;
    popupContainer.dataset.needsLoading = 'true';

    return popupContainer;
};

// ─── loadAdditionalDataAsync ──────────────────────────────────────────────────

export async function loadAdditionalDataAsync(lat, lng, osmid, popupContainer, btnAddUpdate) {
    const additionalTabBtn = popupContainer.querySelector('#tab-additional-btn');
    const additionalContent = popupContainer.querySelector('[data-tab-content="additional"]');

    if (!document.body.contains(popupContainer)) return;

    try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000);

        const response = await fetch(`${API_BASE_URL}/api/poi-submissions/by-location?lat=${lat}&lng=${lng}`, { signal: controller.signal });
        clearTimeout(timeoutId);

        if (!document.body.contains(popupContainer)) return;

        const result = await response.json();

        if (result.found && result.data) {
            const data = result.data;

            const hasRealData = Boolean(
                (data.description && data.description.trim()) ||
                (data.phone && data.phone.trim()) ||
                (data.email && data.email.trim()) ||
                (data.website && data.website.trim()) ||
                (data.instagram && data.instagram.trim()) ||
                (data.facebook && data.facebook.trim()) ||
                (data.booking_system && data.booking_system.trim()) ||
                (data.tipologia && data.tipologia.trim()) ||
                (data.servizi_offerti && Array.isArray(data.servizi_offerti) && data.servizi_offerti.length > 0) ||
                (data.come_raggiungere && data.come_raggiungere.trim()) ||
                (data.orari && data.orari.trim()) ||
                (data.periodo_apertura && data.periodo_apertura.trim()) ||
                (data.pernottamento && data.pernottamento.trim()) ||
                (data.tariffe_indicative && data.tariffe_indicative.trim()) ||
                (data.photos && Array.isArray(data.photos) && data.photos.length > 0)
            );

            if (hasRealData) {
                let additionalHTML = '';

                // Foto
                if (data.photos && data.photos.length > 0) {
                    additionalHTML += `
                        <div style="margin-bottom: 12px;">
                            <strong style="display: block; margin-bottom: 6px; color: #333; font-size: 12px;">📸 Foto (${data.photos.length})</strong>
                            <div style="display: grid; grid-template-columns: repeat(auto-fill, minmax(90px, 1fr)); gap: 6px;">
                                ${data.photos.map((photo, i) => `
                                    <a href="${photo.directUrl || photo.url}" target="_blank" rel="noopener noreferrer"
                                        style="display: block; overflow: hidden; border-radius: 4px; box-shadow: 0 1px 3px rgba(0,0,0,0.1);">
                                        <img src="${photo.thumbnailUrl || photo.directUrl}" alt="Foto ${i + 1}" loading="lazy"
                                            style="width: 100%; height: 90px; object-fit: cover; display: block;"
                                            onerror="this.style.display='none';" />
                                    </a>`).join('')}
                            </div>
                        </div>`;
                }

                if (data.description) additionalHTML += `
                    <div style="margin-bottom: 10px; padding: 8px; background: #f9f9f9; border-radius: 4px; border-left: 2px solid #4CAF50;">
                        <strong style="color: #4CAF50; font-size: 12px;">📝 Descrizione</strong>
                        <p style="margin: 4px 0 0 0; font-size: 12px; line-height: 1.5; color: #333;">${data.description}</p>
                    </div>`;

                if (data.tipologia) additionalHTML += `<p style="margin: 6px 0; font-size: 12px;"><strong>🏷️ Tipologia:</strong> ${data.tipologia}</p>`;

                if (data.servizi_offerti && data.servizi_offerti.length > 0) additionalHTML += `
                    <div style="margin: 10px 0;">
                        <strong style="font-size: 12px;">✨ Servizi:</strong>
                        <ul style="margin: 4px 0; padding-left: 18px; font-size: 12px;">
                            ${data.servizi_offerti.map(s => `<li>${s}</li>`).join('')}
                        </ul>
                    </div>`;

                if (data.come_raggiungere) additionalHTML += `
                    <div style="margin: 10px 0; padding: 8px; background: #f9f9f9; border-radius: 4px;">
                        <strong style="font-size: 12px;">🗺️ Come raggiungere</strong>
                        <p style="margin: 4px 0 0 0; font-size: 12px;">${data.come_raggiungere}</p>
                    </div>`;

                if (data.orari) additionalHTML += `<p style="margin: 6px 0; font-size: 12px;"><strong>🕐 Orari:</strong> ${data.orari}</p>`;
                if (data.periodo_apertura) additionalHTML += `<p style="margin: 6px 0; font-size: 12px;"><strong>📅 Periodo:</strong> ${data.periodo_apertura}</p>`;
                if (data.pernottamento) additionalHTML += `<p style="margin: 6px 0; font-size: 12px;"><strong>🛏️ Pernottamento:</strong> ${data.pernottamento}</p>`;
                if (data.tariffe_indicative) additionalHTML += `<p style="margin: 6px 0; font-size: 12px;"><strong>💰 Tariffe:</strong> ${data.tariffe_indicative}</p>`;

                // Telefono (con WhatsApp per cellulari)
                const waHref = `${API_NODE_BASE}/go/wa?osmid=${encodeURIComponent(osmid)}&lat=${encodeURIComponent(lat)}&lng=${encodeURIComponent(lng)}`;
                if (data.phone) {
                    const parts = splitPhones(data.phone);
                    let hasMobileShown = false;
                    for (const part of parts) {
                        const digits = normalizeDigits(part);
                        const isMobile = isItalianMobileDigits(digits);
                        if (isMobile && !hasMobileShown) {
                            additionalHTML += `
                                <p style="margin: 6px 0; font-size: 12px;">
                                    <strong>💬 WhatsApp:</strong>
                                    <button class="wa-open-btn" data-href="${waHref}"
                                        style="background:none;border:none;padding:0;color:#25D366;font-weight:500;font-size:12px;cursor:pointer;text-decoration:underline;">
                                        Scrivi su WhatsApp
                                    </button>
                                </p>`;
                            hasMobileShown = true;
                        }
                        additionalHTML += `
                            <p style="margin: 6px 0; font-size: 12px;">
                                <strong>${isMobile ? '📱 Cellulare' : '📞 Telefono'}:</strong>
                                <button class="phone-open-btn"
                                    data-phone="${part}" data-osmid="${osmid}" data-lat="${lat}" data-lng="${lng}"
                                    style="background:none;border:none;padding:0;color:#2196F3;font-weight:500;font-size:12px;cursor:pointer;text-decoration:underline;">
                                    ${part}
                                </button>
                            </p>`;
                    }
                }

                // Email
                if (data.email) additionalHTML += `
                    <p style="margin: 6px 0; font-size: 12px;">
                        <strong>📧 Email:</strong>
                        <button class="email-open-btn"
                            data-email="${data.email}" data-osmid="${osmid}" data-lat="${lat}" data-lng="${lng}"
                            style="background:none;border:none;padding:0;color:#2196F3;font-weight:500;font-size:12px;cursor:pointer;text-decoration:underline;">
                            ${data.email}
                        </button>
                    </p>`;

                // Website — tracciato
                if (data.website) additionalHTML += `
                    <p style="margin: 6px 0; font-size: 12px;">
                        <strong>🌐 Sito:</strong>
                        <button class="website-open-btn"
                            data-website="${data.website}" data-osmid="${osmid}" data-lat="${lat}" data-lng="${lng}"
                            style="background:none;border:none;padding:0;color:#2196F3;font-weight:500;font-size:12px;cursor:pointer;text-decoration:underline;">
                            Visita sito
                        </button>
                    </p>`;

                if (data.instagram) additionalHTML += `<p style="margin: 6px 0; font-size: 12px;"><strong>📸 Instagram:</strong> <a href="${data.instagram}" target="_blank" style="color:#2196F3;">Profilo</a></p>`;
                if (data.facebook) additionalHTML += `<p style="margin: 6px 0; font-size: 12px;"><strong>👥 Facebook:</strong> <a href="${data.facebook}" target="_blank" style="color:#2196F3;">Pagina</a></p>`;
                if (data.booking_system) additionalHTML += `<p style="margin: 6px 0; font-size: 12px;"><strong>📅 Prenotazioni:</strong> <a href="${data.booking_system}" target="_blank" style="color:#2196F3;">Sistema</a></p>`;

                additionalHTML += `<p style="font-size:10px;color:#999;margin-top:12px;padding-top:10px;border-top:1px solid #eee;">✏️ Aggiunto il ${new Date(data.submitted_at).toLocaleDateString('it-IT')}</p>`;

                additionalContent.innerHTML = additionalHTML;

                // Event listeners additional tab
                additionalContent.querySelectorAll('.wa-open-btn').forEach(btn => {
                    btn.addEventListener('click', (e) => {
                        e.preventDefault(); e.stopPropagation();
                        window.open(btn.getAttribute('data-href'), '_blank', 'noopener,noreferrer');
                    });
                });

                additionalContent.querySelectorAll('.phone-open-btn').forEach(btn => {
                    btn.addEventListener('click', (e) => {
                        e.preventDefault(); e.stopPropagation();
                        window.location.href = `tel:${btn.getAttribute('data-phone')}`;
                        trackContact('phone', btn.getAttribute('data-osmid'), btn.getAttribute('data-lat'), btn.getAttribute('data-lng'));
                    });
                });

                additionalContent.querySelectorAll('.email-open-btn').forEach(btn => {
                    btn.addEventListener('click', (e) => {
                        e.preventDefault(); e.stopPropagation();
                        window.location.href = `mailto:${btn.getAttribute('data-email')}`;
                        trackContact('email', btn.getAttribute('data-osmid'), btn.getAttribute('data-lat'), btn.getAttribute('data-lng'));
                    });
                });

                additionalContent.querySelectorAll('.website-open-btn').forEach(btn => {
                    btn.addEventListener('click', (e) => {
                        e.preventDefault(); e.stopPropagation();
                        window.open(btn.getAttribute('data-website'), '_blank', 'noopener,noreferrer');
                        trackContact('website', btn.getAttribute('data-osmid'), btn.getAttribute('data-lat'), btn.getAttribute('data-lng'));
                    });
                });

                if (btnAddUpdate) btnAddUpdate.innerHTML = '✏️ Aggiorna';

            } else {
                if (additionalTabBtn) additionalTabBtn.style.display = 'none';
                if (btnAddUpdate) btnAddUpdate.innerHTML = '✏️ Aggiungi';
                additionalContent.innerHTML = `<div style="text-align:center;padding:20px;color:#999;"><p style="margin:0;font-size:13px;">Nessuna informazione aggiuntiva disponibile</p></div>`;
            }
        } else {
            if (additionalTabBtn) additionalTabBtn.style.display = 'none';
            if (btnAddUpdate) btnAddUpdate.innerHTML = '✏️ Aggiungi';
            additionalContent.innerHTML = `<div style="text-align:center;padding:20px;color:#999;"><p style="margin:0;font-size:13px;">Nessuna informazione aggiuntiva disponibile</p></div>`;
        }

    } catch (error) {
        if (error.name === 'AbortError') return;
        if (!additionalContent || !document.body.contains(popupContainer)) return;
        additionalContent.innerHTML = `
            <div style="text-align:center;padding:20px;">
                <p style="color:#f44336;font-size:13px;margin:0 0 10px 0;">❌ Errore durante il caricamento</p>
                <p style="color:#999;font-size:11px;margin:0;">${error.message || 'Errore sconosciuto'}</p>
            </div>`;
    }
}

// ─── POI Filtering utilities ──────────────────────────────────────────────────

// Normalizza testo: lowercase, trim, rimuove accenti, comprime spazi
export const normalizeText = (value) => {
    if (!value) return '';
    return String(value)
        .toLowerCase()
        .trim()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\s+/g, ' ');
};

// Verifica se una feature è un POI (Point)
export const isPoiFeature = (feature) => {
    return feature?.geometry?.type === 'Point';
};

// Estrae coordinate [lon, lat] da una feature Point
export const extractPointFromFeature = (feature) => {
    if (feature?.geometry?.type !== 'Point') return null;
    const [lon, lat] = feature.geometry.coordinates || [];
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) return null;
    return [lon, lat];
};

// Legge comune/provincia dalle properties OSM
export const getComuneProvinciaFromProperties = (feature) => {
    const props = feature.properties || {};

    // Helper per validare e pulire valori stringa
    const safe = (value) => typeof value === 'string' ? value.trim() : '';

    const comune =
        safe(props['addr:city']) ||
        safe(props.city) ||
        safe(props.town) ||
        safe(props.village) ||
        safe(props.municipality);

    const provincia =
        safe(props.province) ||
        safe(props['addr:province']) ||
        safe(props.provincia);

    const siglaProvincia = safe(props['addr:province:short']);

    return {
        comune,
        provincia,
        siglaProvincia
    };
};

// Lookup geografico: trova comune/provincia per coordinate usando bbox + booleanPointInPolygon
export const findComuneByPoint = (lon, lat, istatItems, cache = null) => {
    const cacheKey = `${lon},${lat}`;
    if (cache?.has(cacheKey)) {
        return cache.get(cacheKey);
    }

    if (!istatItems || istatItems.length === 0) return null;

    const pt = point([lon, lat]);

    for (const item of istatItems) {
        if (!item.bbox || !item.geometry) continue;

        const [minLon, minLat, maxLon, maxLat] = item.bbox;
        const isInsideBBox =
            lon >= minLon &&
            lon <= maxLon &&
            lat >= minLat &&
            lat <= maxLat;

        if (!isInsideBBox) continue;

        const polygonFeature = {
            type: 'Feature',
            geometry: item.geometry,
            properties: {}
        };

        if (booleanPointInPolygon(pt, polygonFeature)) {
            const result = {
                comune: item.comune || '',
                provincia: item.provincia || '',
                siglaProvincia: item.siglaProvincia || '',
                codiceProvincia: item.codiceProvincia || '',
                bbox: item.bbox,
                geometry: item.geometry
            };

            if (cache) cache.set(cacheKey, result);
            return result;
        }
    }

    if (cache) cache.set(cacheKey, null);
    return null;
};

// Mappa tipologia leggibile per il filtro
export const mapPoiType = (feature) => {
    const props = feature.properties || {};

    const tourism = props.tourism;
    const amenity = props.amenity;
    const leisure = props.leisure;
    const naturalType = props.natural;

    // Rifugio
    if (tourism === 'alpine_hut' || tourism === 'wilderness_hut') return t('rifugio');

    // Ristorazione
    if (amenity === 'restaurant' || amenity === 'bar' || amenity === 'cafe' || amenity === 'pub') return t('ristorazione');

    // Alloggio
    if (tourism === 'hotel' || tourism === 'guest_house' || tourism === 'chalet' || tourism === 'apartment') return t('alloggio');

    // Acqua
    if (amenity === 'drinking_water' || naturalType === 'spring') return 'Acqua';

    // Parcheggio
    if (amenity === 'parking') return 'Parcheggio';

    // Punto panoramico
    if (tourism === 'viewpoint') return 'Punto panoramico';

    // Campeggio
    if (tourism === 'camp_site' || tourism === 'caravan_site') return 'Campeggio';

    // Fallback
    return t('altro');
};

// Applica i filtri POI a un array di feature
export const applyPoiFilters = (features, poiFilters) => {
    if (!Array.isArray(features)) return [];

    // Pre-normalizza i filtri una sola volta
    const filters = {
        name: normalizeText(poiFilters.name.trim()),
        comune: normalizeText(poiFilters.comune.trim()),
        provincia: normalizeText(poiFilters.provincia.trim()),
        tipologie: poiFilters.tipologie || []
    };

    // Niente filtri attivi = return tutti
    if (!filters.name && !filters.comune && !filters.provincia && filters.tipologie.length === 0) {
        return features;
    }

    return features.filter(feature => {
        const props = feature.properties || {};

        // Filtro Nome - usa il campo pre-normalizzato
        if (filters.name) {
            const normalized = props.normalizedName || '';
            if (!normalized.includes(filters.name)) return false;
        }

        // Filtro Comune - usa il campo pre-normalizzato
        if (filters.comune) {
            const normalized = props.normalizedComune || '';
            if (!normalized.includes(filters.comune)) return false;
        }

        // Filtro Provincia - usa il campo pre-normalizzato
        if (filters.provincia) {
            const normalized = props.normalizedProvincia || '';
            if (!normalized.includes(filters.provincia)) return false;
        }

        // Filtro Tipologia - match esatto
        if (filters.tipologie.length > 0) {
            const tipologia = props.tipologiaFinale || 'Altro';
            if (!filters.tipologie.includes(tipologia)) return false;
        }

        return true;
    });
};

// Filtra POI visibili nel viewport della mappa, limitati a N risultati
export const filterPoiByMapBounds = (features, mapBounds, limit = 50) => {
    if (!mapBounds || !Array.isArray(features)) return [];

    // Estrai i bounds da Leaflet: { _northEast: {lat, lng}, _southWest: {lat, lng} }
    const { _northEast, _southWest } = mapBounds;
    if (!_northEast || !_southWest) return [];

    // Filtra le feature che rientrano nei bounds attuali
    const visiblePois = features.filter(feature => {
        const coords = extractPointFromFeature(feature);
        if (!coords) return false;

        const [lon, lat] = coords;
        const isInsideViewport =
            lat >= _southWest.lat && lat <= _northEast.lat &&
            lon >= _southWest.lng && lon <= _northEast.lng;

        return isInsideViewport;
    });

    // Log per debuggare
    if (visiblePois.length > limit) {
        console.info(`ℹ️ POI nel viewport: ${visiblePois.length}, limitati a ${limit}`);
    }

    return visiblePois.slice(0, limit);
};
